import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { Layout } from "components/Layout";
import { memo } from "react";
import { ReactComponent as Ai } from "assets/ai.svg";
import { EmailVerifiedModal } from "./EmailVerifiedModal";

interface VerificationStepProps {
  verifiedEmail: string;
  code: string[];
  processing: boolean;
  showSuccessModal: boolean;
  onCodeChange: (index: number, value: string) => void;
  onCodeKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  onVerifyCode: () => void;
  onResendCode: () => void;
  onChangeEmail: () => void;
  onSuccessModalClose: () => void;
}

export const VerificationStep = memo(function VerificationStep({
  verifiedEmail,
  code,
  processing,
  showSuccessModal,
  onCodeChange,
  onCodeKeyDown,
  onVerifyCode,
  onResendCode,
  onChangeEmail,
  onSuccessModalClose,
}: VerificationStepProps) {
  const isCodeComplete = code.every((digit) => digit !== "");

  return (
    <>
      <Layout
        className="h-full flex items-start justify-center pt-12"
        toolbar={
          <>
            <Button
              disabled={!isCodeComplete || processing}
              type="button"
              onClick={onVerifyCode}
            >
              {processing ? "Verifying..." : "Continue"}
            </Button>

            <Button
              variant={ButtonVariant.FLAT}
              type="button"
              onClick={onChangeEmail}
              disabled={processing}
            >
              Back
            </Button>
          </>
        }
      >
        <div className="w-full max-w-md px-6 space-y-6">
          <div className="text-center">
            <Ai className="mx-auto" />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Verify Your Email
            </h1>

            <div className="space-y-2 mt-6">
              <p className="text-858494">
                We have sent a verification code to:
              </p>
              <p className="text-16 font-semibold text-gray-900">
                {verifiedEmail}
              </p>
              <p className="text-14 text-858494 mt-4">
                Please enter the code here:
              </p>
            </div>
          </div>

          <div className="flex justify-center gap-3">
            {code.map((digit, index) => (
              <Input
                key={index}
                id={`code-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => onCodeChange(index, e.target.value)}
                onKeyDown={(e) => onCodeKeyDown(index, e)}
                autoFocus={index === 0}
                className="w-12 h-12 text-center text-lg font-semibold"
              />
            ))}
          </div>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={onResendCode}
              className="text-14 text-0f24e2 hover:underline"
            >
              Didn't get an email? Resend
            </button>
            <br />
            <button
              type="button"
              onClick={onChangeEmail}
              className="text-14 text-0f24e2 hover:underline"
            >
              Change email
            </button>
          </div>
        </div>
      </Layout>

      <EmailVerifiedModal
        show={showSuccessModal}
        onClose={onSuccessModalClose}
      />
    </>
  );
});
